/**
 * Trainova AI — Production Language Selector Component
 *
 * Integrates into:
 * - AppLayout header
 * - ResponsiveNavDrawer (mobile viewports)
 * - AuthLayout / Public landing
 *
 * Adheres to:
 * - Trainova design tokens
 * - Tenant isolation: shows only enabled tenant languages
 * - Instant reactivity with zero layout shift
 */

import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';

interface LanguageSelectorProps {
  variant?: 'header' | 'drawer' | 'compact';
  className?: string;
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  variant = 'header',
  className = '',
}) => {
  const {
    currentLanguage,
    setLanguage,
    availableLanguages,
    allowUserLanguageSelection,
    isRTL,
    t,
  } = useLanguage();

  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // If tenant disables user switching and only 1 language is available, don't display interactive selector
  if (!allowUserLanguageSelection && availableLanguages.length <= 1) {
    return null;
  }

  const currentMeta = availableLanguages.find((l) => l.code === currentLanguage) || {
    code: currentLanguage,
    name: currentLanguage.toUpperCase(),
    nativeName: currentLanguage.toUpperCase(),
    flag: '🌐',
  };

  const handleSelect = async (code: string) => {
    await setLanguage(code);
    setIsOpen(false);
  };

  if (variant === 'drawer') {
    return (
      <div className={`trainova-lang-drawer ${className}`} style={{ padding: '8px 12px' }}>
        <div
          style={{
            fontSize: '11px',
            textTransform: 'uppercase',
            fontWeight: 700,
            letterSpacing: '0.05em',
            color: 'var(--muted)',
            marginBottom: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <span>🌐</span>
          <span>{t('nav.language', undefined, 'Language')}</span>
        </div>
        <select
          value={currentLanguage}
          onChange={(e) => handleSelect(e.target.value)}
          disabled={!allowUserLanguageSelection}
          aria-label={t('nav.language', undefined, 'Select Display Language')}
          style={{
            width: '100%',
            height: '44px',
            minHeight: '44px',
            padding: '8px 12px',
            borderRadius: '8px',
            border: '1px solid var(--line)',
            background: 'var(--white)',
            color: 'var(--ink)',
            fontSize: '15px',
            fontWeight: 500,
            cursor: allowUserLanguageSelection ? 'pointer' : 'not-allowed',
            outline: 'none',
          }}
        >
          {availableLanguages.map((lang) => (
            <option key={lang.code} value={lang.code}>
              {lang.flag} {lang.nativeName} ({lang.name})
            </option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`trainova-lang-selector trainova-lang-selector--${variant} ${className}`}
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
    >
      <button
        type="button"
        className="trainova-lang-btn"
        onClick={() => {
          if (allowUserLanguageSelection) {
            setIsOpen(!isOpen);
          }
        }}
        disabled={!allowUserLanguageSelection}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`${t('nav.language', undefined, 'Language')}: ${currentMeta.nativeName}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          height: '38px',
          minHeight: '38px',
          padding: variant === 'compact' ? '4px 8px' : '6px 12px',
          borderRadius: '8px',
          border: '1px solid var(--line)',
          background: isOpen ? 'var(--blue-soft)' : 'var(--white)',
          color: isOpen ? 'var(--blue)' : 'var(--ink)',
          fontSize: '13px',
          fontWeight: 600,
          cursor: allowUserLanguageSelection ? 'pointer' : 'default',
          transition: 'all 0.15s ease',
          outline: 'none',
          boxShadow: '0 1px 2px rgba(7, 29, 69, 0.04)',
        }}
      >
        <span className="trainova-lang-flag" style={{ fontSize: '15px', lineHeight: 1 }}>{currentMeta.flag || '🌐'}</span>
        {variant !== 'compact' && (
          <>
            <span className="trainova-lang-name-full" style={{ whiteSpace: 'nowrap' }}>{currentMeta.nativeName}</span>
            <span className="trainova-lang-name-short" style={{ whiteSpace: 'nowrap' }}>{currentMeta.code.toUpperCase()}</span>
          </>
        )}
        {allowUserLanguageSelection && (
          <svg
            className="trainova-lang-chevron"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
              opacity: 0.7,
              flexShrink: 0,
            }}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        )}
      </button>

      {isOpen && (
        <div
          role="listbox"
          className="trainova-lang-dropdown"
          aria-label={t('nav.language', undefined, 'Available Languages')}
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            [isRTL ? 'left' : 'right']: 0,
            minWidth: '200px',
            maxHeight: '320px',
            overflowY: 'auto',
            background: 'var(--white)',
            borderRadius: '10px',
            border: '1px solid var(--line)',
            boxShadow: '0 8px 24px rgba(7, 29, 69, 0.12)',
            padding: '6px',
            zIndex: 1500,
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
          }}
        >
          <div
            style={{
              padding: '6px 10px 4px 10px',
              fontSize: '11px',
              fontWeight: 700,
              textTransform: 'uppercase',
              color: 'var(--muted)',
              borderBottom: '1px solid var(--line)',
              marginBottom: '4px',
              letterSpacing: '0.04em',
            }}
          >
            {t('nav.language', undefined, 'Select Language')}
          </div>
          {availableLanguages.map((lang) => {
            const isSelected = lang.code === currentLanguage;
            return (
              <button
                key={lang.code}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => handleSelect(lang.code)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  padding: '8px 10px',
                  borderRadius: '6px',
                  border: 'none',
                  background: isSelected ? 'var(--blue-soft)' : 'transparent',
                  color: isSelected ? 'var(--blue)' : 'var(--ink)',
                  fontSize: '13px',
                  fontWeight: isSelected ? 700 : 500,
                  cursor: 'pointer',
                  textAlign: isRTL ? 'right' : 'left',
                  transition: 'background 0.15s ease',
                  minHeight: '40px',
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = 'var(--surface)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '15px' }}>{lang.flag}</span>
                  <div>
                    <span style={{ display: 'block', lineHeight: 1.2 }}>{lang.nativeName}</span>
                    <span
                      style={{
                        display: 'block',
                        fontSize: '11px',
                        color: isSelected ? 'var(--blue)' : 'var(--muted)',
                        opacity: 0.85,
                      }}
                    >
                      {lang.name}
                    </span>
                  </div>
                </div>
                {isSelected && (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--blue)"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
